import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  MemoryRouter,
  Navigate,
  Route,
  Routes,
  UNSAFE_LocationContext,
  UNSAFE_RouteContext,
  useLocation,
  useNavigate,
  useParams
} from "react-router-dom";
import { BOARD_OVERLAY_ROUTE_DEFS, BoardRouteDef } from "../../../management/board/board-overlay-routes";
import { Project } from "../../../management/board/types";
import { useBoardConfig } from "../../../management/board/use-board";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import "./board-overlay.scss";

// React Router forbids a <Router> inside another <Router>, and React context flows through portals,
// so the overlay's MemoryRouter would otherwise see the app's BrowserRouter and throw. Resetting
// LocationContext to null at the boundary (null is its real default — RR creates it with
// `createContext(null!)`) makes the MemoryRouter a fresh, independent router root. This is the
// supported way to embed an isolated router region. The `unknown` hop only satisfies the context's
// non-null value type; the value the library expects at runtime is genuinely null.
const NO_OUTER_LOCATION: React.ContextType<typeof UNSAFE_LocationContext> = null as unknown as React.ContextType<
  typeof UNSAFE_LocationContext
>;

// And clear the outer route match, so the overlay's <Routes> is treated as a root — not a descendant
// of the designer's "/:domainName/sd/:sdId" route (which would warn and fail to match deeper paths).
// This is RouteContext's real default value.
const ROOT_ROUTE_CONTEXT: React.ContextType<typeof UNSAFE_RouteContext> = {
  outlet: null,
  matches: [],
  isDataRoute: false
};

// Resizable width (drag the right edge), persisted across opens.
const WIDTH_STORAGE_KEY: string = "kuika-board-overlay-width";
const TOOLBAR_OFFSET: number = 52; // the design toolbar rail the panel sits beside
const MIN_WIDTH: number = 640;
const DEFAULT_WIDTH: number = 980;

function clampWidth(width: number): number {
  const max: number = Math.max(MIN_WIDTH, window.innerWidth - TOOLBAR_OFFSET - 64);
  return Math.min(Math.max(width, MIN_WIDTH), max);
}

function readStoredWidth(): number {
  const raw: string | null = window.localStorage.getItem(WIDTH_STORAGE_KEY);
  const parsed: number = raw ? Number.parseInt(raw, 10) : DEFAULT_WIDTH;
  return clampWidth(Number.isFinite(parsed) ? parsed : DEFAULT_WIDTH);
}

export interface BoardOverlayProps {
  // The designer's active platform project id, used to land on its linked board project.
  projectId?: string;
  onClose: () => void;
}

// Reports the MemoryRouter's current location up so the header's "Open in board view" action can
// promote exactly what's on screen to the real route. Rendered inside the MemoryRouter.
interface LocationSyncProps {
  onPathChange: (path: string) => void;
}

const BoardOverlayLocationSync: React.FC<LocationSyncProps> = ({ onPathChange }) => {
  const location = useLocation();
  useEffect((): void => {
    onPathChange(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search, onPathChange]);
  return null;
};

// Opens the Kanban board as a left slide-over inside the screen designer. The board runs as real
// components (not an iframe) under an isolated MemoryRouter, so navigating projects/boards/issues
// here never changes the designer's URL; "Open in board view" promotes the current in-overlay
// location to the real /board route. It lands on the project being designed (matched by platform
// project id), falling back to the projects index.
const BoardOverlay: React.FC<BoardOverlayProps> = ({ projectId, onClose }) => {
  const { domainName } = useParams<{ domainName: string }>();
  const domain: string = domainName ?? "";
  const indexPath: string = `/${domain}/board`;
  const { config, loaded } = useBoardConfig();
  const navigate = useNavigate();
  const [currentPath, setCurrentPath] = useState<string>("");
  const [width, setWidth] = useState<number>(() => readStoredWidth());
  const [resizing, setResizing] = useState<boolean>(false);

  // Persist the chosen width (cheap; localStorage writes are fine even mid-drag).
  useEffect((): void => {
    window.localStorage.setItem(WIDTH_STORAGE_KEY, String(Math.round(width)));
  }, [width]);

  // Drag the right edge to resize. Listeners live on the document so the pointer can leave the thin
  // handle without dropping the drag; the body's user-select is suspended so text isn't selected.
  const startResize = useCallback(
    (event: React.MouseEvent): void => {
      event.preventDefault();
      const startX: number = event.clientX;
      const startWidth: number = width;
      setResizing(true);
      document.body.style.userSelect = "none";
      const onMove = (move: MouseEvent): void => {
        setWidth(clampWidth(startWidth + (move.clientX - startX)));
      };
      const onUp = (): void => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
        document.body.style.userSelect = "";
        setResizing(false);
      };
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },
    [width]
  );

  // The board project linked to the designer project being worked on (1:1 via platform project id).
  const linkedProject: Project | undefined = useMemo(
    (): Project | undefined =>
      projectId ? config.projects.find((entry: Project) => entry.platformProjectId === projectId) : undefined,
    [config.projects, projectId]
  );

  // This designer project has no board project — boards aren't enabled for it. Show a notice rather
  // than silently dropping to the index, so it's clear why the board isn't here.
  const notEnabled: boolean = loaded && Boolean(projectId) && !linkedProject;

  // Land on the linked project's board, else the index. Computed once config is in; until then the
  // body shows a loading state (the MemoryRouter's initial entry must be right on first mount).
  const landingPath: string | null = useMemo((): string | null => {
    if (!loaded) return null;
    return linkedProject ? `/${domain}/board/${linkedProject.key}` : indexPath;
  }, [loaded, linkedProject, domain, indexPath]);

  // The project whose board is currently on screen (tracks in-overlay navigation), for the header
  // breadcrumb. Null on the projects index or a multi-project board.
  const activeProject: Project | undefined = useMemo((): Project | undefined => {
    const path: string = currentPath || landingPath || "";
    const match: RegExpMatchArray | null = path.match(/\/board\/([^/?#]+)/);
    const segment: string | undefined = match?.[1];
    if (!segment || segment === "multi") return undefined;
    return config.projects.find((entry: Project) => entry.key === segment);
  }, [currentPath, landingPath, config.projects]);

  // Close on Escape.
  useEffect((): (() => void) => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  // Board popups (antd dropdowns, the AI modal via rc-dialog, the board's portaled menus) render to
  // document.body, so they're siblings of this overlay and would fall behind it. Flag the open state
  // on <body> so a scoped stylesheet can lift those popup layers above the panel (see the .scss).
  useEffect((): (() => void) => {
    document.body.classList.add("kuika-board-overlay-open");
    return () => document.body.classList.remove("kuika-board-overlay-open");
  }, []);

  const openInBoardView = (): void => {
    navigate(currentPath || landingPath || indexPath);
    onClose();
  };

  return createPortal(
    <div className="kuika-board-overlay" data-testid="board-overlay">
      <div className="kuika-board-overlay__backdrop" onClick={onClose} />
      <div
        className={`kuika-board-overlay__panel${resizing ? " kuika-board-overlay__panel--resizing" : ""}`}
        style={{ width }}
        role="dialog"
        aria-label="Board"
      >
        <div className="kuika-board-overlay__header">
          <span className="kuika-board-overlay__title">
            <span className="kuika-board-overlay__title-icon">
              <KuikaSvg type="sidebar-icons" name="board" width={14} height={14} />
            </span>
            <span className="kuika-board-overlay__title-text">Board</span>
            {activeProject && (
              <>
                <span className="kuika-board-overlay__crumb-sep" aria-hidden="true">
                  /
                </span>
                <span
                  className="kuika-board-overlay__crumb-dot"
                  style={{ background: activeProject.color }}
                  aria-hidden="true"
                />
                <span className="kuika-board-overlay__crumb-name">{activeProject.name}</span>
              </>
            )}
          </span>
          <div className="kuika-board-overlay__actions">
            <button
              type="button"
              className="kuika-board-overlay__open-full"
              onClick={openInBoardView}
              title="Open the full board view"
              data-testid="board-overlay-open-full"
            >
              <KuikaSvg type="sidebar-icons" name="layout-sidebar-left-expand" width={15} height={15} />
              Open in board view
            </button>
            <button
              type="button"
              className="kuika-board-overlay__close"
              onClick={onClose}
              aria-label="Close board"
              data-testid="board-overlay-close"
            >
              <KuikaSvg name="x" width={16} height={16} />
            </button>
          </div>
        </div>
        <div className="kuika-board-overlay__body">
          {landingPath === null ? (
            <div className="kuika-board-overlay__loading">Loading board…</div>
          ) : notEnabled ? (
            <div className="kuika-board-overlay__notice">
              <div className="kuika-board-overlay__notice-icon">
                <KuikaSvg type="sidebar-icons" name="board" width={22} height={22} />
              </div>
              <div className="kuika-board-overlay__notice-title">Boards aren’t enabled for this project</div>
              <div className="kuika-board-overlay__notice-text">
                {config.canManage
                  ? "Enable it from the board home to start tracking issues for this project."
                  : "Ask a workspace owner or supervisor to enable it to start tracking issues for this project."}
              </div>
              <button
                type="button"
                className="kuika-board-overlay__notice-action"
                onClick={() => {
                  navigate(indexPath);
                  onClose();
                }}
              >
                Go to board home
              </button>
            </div>
          ) : (
            <UNSAFE_RouteContext.Provider value={ROOT_ROUTE_CONTEXT}>
              <UNSAFE_LocationContext.Provider value={NO_OUTER_LOCATION}>
                <MemoryRouter initialEntries={[landingPath]}>
                  <BoardOverlayLocationSync onPathChange={setCurrentPath} />
                  <Routes>
                    {BOARD_OVERLAY_ROUTE_DEFS.map((def: BoardRouteDef) => (
                      <Route key={def.path} path={def.path} element={def.element} />
                    ))}
                    <Route path="*" element={<Navigate to={indexPath} replace />} />
                  </Routes>
                </MemoryRouter>
              </UNSAFE_LocationContext.Provider>
            </UNSAFE_RouteContext.Provider>
          )}
        </div>
        <div
          className="kuika-board-overlay__resize"
          onMouseDown={startResize}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize board panel"
        />
      </div>
    </div>,
    document.body
  );
};

export default BoardOverlay;
